/**
 * Data management utilities for Mario Kart World guide
 * Handles loading and accessing track and intermission data
 */

import tracksJson from '../data/tracks.json';
import intermissionsJson from '../data/intermissions.json';
import shortcutsJson from '../data/shortcuts.json';

let tracksData = tracksJson;
let intermissionsData = intermissionsJson;
let shortcutsData = shortcutsJson;

/**
 * Initialize data by loading JSON files
 */
export async function initializeData() {
  console.log(`Loaded ${tracksData.tracks.length} tracks, ${intermissionsData.intermissions.length} intermissions, and ${Object.keys(shortcutsData.shortcuts).length} shortcuts`);
  return true;
}

/**
 * Get all tracks
 * @returns {Array} Array of track objects
 */
export function getAllTracks() {
  return tracksData?.tracks || [];
}

/**
 * Get a track by ID
 * @param {string} trackId - The track ID
 * @returns {Object|null} Track object or null if not found (with resolved shortcuts)
 */
export function getTrackById(trackId) {
  const track = tracksData?.tracks.find(track => track.id === trackId) || null;
  if (track && track.standardRace) {
    return {
      ...track,
      standardRace: {
        ...track.standardRace,
        shortcuts: resolveShortcuts(track.standardRace.shortcuts)
      }
    };
  }
  return track;
}

/**
 * Get all intermissions starting from a specific track
 * Ordered according to the track's intermissions array
 * @param {string} trackId - The starting track ID
 * @returns {Array} Array of intermission objects (with resolved shortcuts)
 */
export function getIntermissionsFrom(trackId) {
  if (!intermissionsData || !tracksData) return [];

  // Get the track to access its intermissions order
  const track = tracksData.tracks.find(t => t.id === trackId);
  if (!track || !track.intermissions) return [];

  // Get all intermissions from this track
  const fromIntermissions = intermissionsData.intermissions
    .filter(intermission => intermission.from === trackId);

  // Create a map for quick lookup
  const intermissionMap = {};
  fromIntermissions.forEach(intermission => {
    intermissionMap[intermission.to] = intermission;
  });

  // Return intermissions in the order specified by the track's array
  return track.intermissions
    .map(toTrackId => intermissionMap[toTrackId])
    .filter(Boolean) // Remove any that don't exist in intermissions.json
    .map(intermission => resolveIntermissionShortcuts(intermission));
}

/**
 * Get a specific intermission by from and to track IDs
 * @param {string} fromId - Starting track ID
 * @param {string} toId - Destination track ID
 * @returns {Object|null} Intermission object or null if not found
 */
export function getIntermission(fromId, toId) {
  if (!intermissionsData) return null;

  return intermissionsData.intermissions.find(
    intermission => intermission.from === fromId && intermission.to === toId
  ) || null;
}

/**
 * Expand any shortcutGroups on an intermission into individual shortcut IDs appended at the end
 * @param {Object} intermission - The intermission object
 * @returns {Object} Intermission with shortcutGroups expanded into shortcuts
 */
function expandShortcutGroups(intermission) {
  const groups = intermission.shortcutGroups || [];
  if (groups.length === 0) return intermission;

  const groupShortcutIds = groups.flatMap(
    groupName => shortcutsData?.groups?.[groupName] || []
  );

  const existingIds = new Set(intermission.shortcuts || []);
  const newIds = groupShortcutIds.filter(id => !existingIds.has(id));

  return {
    ...intermission,
    shortcuts: [...(intermission.shortcuts || []), ...newIds]
  };
}

/**
 * Append shortcuts from destination track's standard race to an intermission
 * Only applies if the intermission ends with the standard variant (no variant or variant="Standard")
 * OR if the intermission has inheritShortcuts: true
 * @param {Object} intermission - The intermission object
 * @returns {Object} Intermission with appended shortcuts
 */
function appendDestinationTrackShortcuts(intermission) {
  // Check if we should inherit shortcuts
  const shouldInherit =
    !intermission.variant ||
    intermission.variant === "Standard" ||
    intermission.inheritShortcuts === true;

  if (!shouldInherit) {
    return intermission;
  }

  // Get the destination track (without resolving shortcuts yet)
  const destinationTrack = tracksData?.tracks.find(t => t.id === intermission.to);
  if (!destinationTrack || !destinationTrack.standardRace) {
    return intermission;
  }

  const trackShortcuts = destinationTrack.standardRace.shortcuts || [];
  const intermissionShortcuts = intermission.shortcuts || [];

  // Deduplicate shortcuts (intermission-specific shortcuts take precedence)
  const seenIds = new Set();
  const combinedShortcuts = [];

  // Add intermission shortcuts first
  for (const id of intermissionShortcuts) {
    if (!seenIds.has(id)) {
      combinedShortcuts.push(id);
      seenIds.add(id);
    }
  }

  // Add track shortcuts, filtering out excluded shortcuts for this variant
  for (const id of trackShortcuts) {
    if (!seenIds.has(id)) {
      // Check if this shortcut should be excluded from this variant
      const shortcutDef = shortcutsData?.shortcuts[id];
      const excludeFromVariants = shortcutDef?.excludeFromVariants || [];

      // Skip if this intermission's variant is in the exclude list
      if (intermission.variant && excludeFromVariants.includes(intermission.variant)) {
        continue;
      }

      combinedShortcuts.push(id);
      seenIds.add(id);
    }
  }

  return {
    ...intermission,
    shortcuts: combinedShortcuts
  };
}

/**
 * Resolve and split an intermission's shortcuts into intermission-specific and inherited (from destination track)
 * Shortcuts shared with the destination track's standard race are always considered inherited (collapsed by default)
 * @param {Object} intermission - The raw intermission object
 * @returns {Object} Intermission with resolved shortcuts, intermissionShortcuts, inheritedShortcuts, destinationTrackName
 */
function resolveIntermissionShortcuts(intermission) {
  const withGroups = expandShortcutGroups(intermission);
  const withTrackShortcuts = appendDestinationTrackShortcuts(withGroups);
  const allShortcutIds = withTrackShortcuts.shortcuts || [];

  const destinationTrack = tracksData?.tracks.find(t => t.id === intermission.to);
  const trackShortcutIds = new Set(destinationTrack?.standardRace?.shortcuts || []);

  const intermissionSpecificIds = allShortcutIds.filter(id => !trackShortcutIds.has(id));
  const inheritedFromTrackIds = allShortcutIds.filter(id => trackShortcutIds.has(id));

  return {
    ...withTrackShortcuts,
    shortcuts: resolveShortcuts(allShortcutIds),
    intermissionShortcuts: resolveShortcuts(intermissionSpecificIds),
    inheritedShortcuts: resolveShortcuts(inheritedFromTrackIds),
    destinationTrackName: intermission.toName
  };
}

/**
 * Get intermission by its unique ID
 * @param {string} intermissionId - The intermission ID
 * @returns {Object|null} Intermission object or null if not found (with resolved shortcuts split into intermission/inherited)
 */
export function getIntermissionById(intermissionId) {
  if (!intermissionsData) return null;

  const intermission = intermissionsData.intermissions.find(
    intermission => intermission.id === intermissionId
  ) || null;

  if (intermission) {
    return resolveIntermissionShortcuts(intermission);
  }

  return null;
}

/**
 * Convert YouTube video ID to embed URL with optional timestamps
 * @param {string} videoId - YouTube video ID
 * @param {number} start - Optional start time in seconds
 * @param {number} end - Optional end time in seconds
 * @returns {string} YouTube embed URL
 */
export function getYouTubeEmbedUrl(videoId, start = null, end = null) {
  const params = [];

  if (start !== null && start > 0) {
    params.push(`start=${start}`);
  }

  if (end !== null && end > 0) {
    params.push(`end=${end}`);
  }

  params.push('enablejsapi=1');

  const queryString = params.length > 0 ? `?${params.join('&')}` : '';
  return `https://www.youtube.com/embed/${videoId}${queryString}`;
}

/**
 * Resolve shortcut IDs to full shortcut objects
 * Handles both arrays of IDs (new format) and arrays of objects (legacy format)
 * @param {Array} shortcuts - Array of shortcut IDs or shortcut objects
 * @returns {Array} Array of resolved shortcut objects
 */
function resolveShortcuts(shortcuts) {
  if (!shortcuts || !Array.isArray(shortcuts)) {
    return [];
  }

  return shortcuts.map(shortcut => {
    // If it's already an object (legacy inline format), return as-is
    if (typeof shortcut === 'object' && shortcut !== null) {
      return shortcut;
    }

    // If it's a string (ID format), resolve from shortcuts library
    if (typeof shortcut === 'string') {
      const resolved = shortcutsData?.shortcuts[shortcut];
      if (!resolved) {
        console.warn(`Shortcut ID "${shortcut}" not found in shortcuts library`);
        return null;
      }
      return resolved;
    }

    return null;
  }).filter(Boolean); // Remove any nulls
}
